import { useEffect, useState } from 'react';
import { Dialog, DialogActions, Button, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import type { IPadLockDetails } from 'api/interfaces/lockerGate';
import { Form, getValidator } from '@sprytki-frontend-apps/common';
import { useTranslation } from 'react-i18next';
import { usePadLock } from '../../alerts.logic';
import { DefaultTextField, DictLockerStatus, DictPadLockMode } from 'shared';
import { StyledDialogContent } from 'components/alerts/StyledComponents';
import { appStore } from 'stores/appStore';
import { locationStore } from 'stores/locationStore/locationStore';
import { DualTitleDialog } from '../DualTitleDialog';
import { observer } from 'mobx-react-lite';

interface PadLockDialogProps {
	id: string;
	userFriendlyId: string;
	lockerStatus?: DictLockerStatus;
	padLockDetails: IPadLockDetails;
	open: boolean;
	onClose: () => void;
}

type PadLockFormValues = {
	padLockCode?: string;
};

type PadLockMode = 'withCode' | 'withoutCode';

export const PadLockDialog = observer(
	({ id, userFriendlyId, padLockDetails, lockerStatus, open, onClose }: PadLockDialogProps) => {
		const { t } = useTranslation();
		const { padLockInPlace, padLockCode: currentPadLockCode } = padLockDetails;
		const isPadLockInPlace = Boolean(padLockInPlace);
		const { handlePadLock } = usePadLock(id, isPadLockInPlace);
		const isLockerRented = lockerStatus === DictLockerStatus.Rented;
		const isPadLockPermanent = locationStore.selectedLocation?.dictPadLockMode === DictPadLockMode.Permanent;
		const showPadLockModeToggle = isLockerRented && !isPadLockInPlace && !isPadLockPermanent;
		const [padLockMode, setPadLockMode] = useState<PadLockMode>('withCode');
		const suggestedPadLockCode = !isPadLockInPlace && currentPadLockCode ? currentPadLockCode : undefined;

		useEffect(() => {
			if (!open || !showPadLockModeToggle) {
				setPadLockMode('withCode');
			}
		}, [open, showPadLockModeToggle]);

		const dialogTitle = isPadLockInPlace
			? `${t('common.buttons.padLock')} - ${t('pages.lockers.padLock.previewCode')}`
			: t('pages.lockers.padLock.lock');

		const handleSubmit = async ({ padLockCode }: PadLockFormValues) => {
			const normalizedCode = showPadLockModeToggle && padLockMode === 'withoutCode' ? '' : padLockCode?.trim();
			const isSuccess = await handlePadLock(normalizedCode);
			if (isSuccess) {
				onClose();
			}
		};

		const shouldRenderPadLockInput = !isPadLockInPlace && (!showPadLockModeToggle || padLockMode === 'withCode');
		const padLockInputRules = shouldRenderPadLockInput ? getValidator(t, 10, null, { value: true }, 1) : undefined;

		return (
			<Dialog open={open} onClose={onClose} fullWidth>
				<Form onSubmit={handleSubmit} resetOnSubmit>
					<DualTitleDialog
						dialogTitle={dialogTitle}
						dialogTitleSecondary={`${t('common.locker')}: ${userFriendlyId}`}
					/>

					<StyledDialogContent>
						{showPadLockModeToggle && (
							<RadioGroup
								row
								value={padLockMode}
								onChange={(_, value) => setPadLockMode(value as PadLockMode)}
							>
								<FormControlLabel
									value='withCode'
									control={<Radio />}
									label={t('pages.lockers.padLock.withCode')}
									disabled={appStore.isLoading}
								/>
								<FormControlLabel
									value='withoutCode'
									control={<Radio />}
									label={t('pages.lockers.padLock.withoutCode')}
									disabled={appStore.isLoading}
								/>
							</RadioGroup>
						)}

						{shouldRenderPadLockInput ? (
							<DefaultTextField
								name='padLockCode'
								label={t('pages.lockers.padLock.enterCode')}
								placeholder={t('pages.lockers.padLock.exampleCode')}
								defaultValue={suggestedPadLockCode}
								fullWidth
								margin='normal'
								disabled={appStore.isLoading}
								rules={padLockInputRules}
							/>
						) : !isPadLockInPlace ? null : currentPadLockCode ? (
							<Typography>
								{t('pages.lockers.padLock.currentCode')} <strong>{currentPadLockCode}</strong>
							</Typography>
						) : (
							<Typography>{t('pages.lockers.padLock.codeNotAvailable')}</Typography>
						)}
					</StyledDialogContent>

					<DialogActions>
						<Button variant='outlined' onClick={onClose} disabled={appStore.isLoading}>
							{t('common.cancel')}
						</Button>
						<Button type='submit' variant='contained' color='primary' disabled={appStore.isLoading}>
							{isPadLockInPlace ? t('pages.lockers.padLock.unlock') : t('common.save')}
						</Button>
					</DialogActions>
				</Form>
			</Dialog>
		);
	}
);
